import React, { useEffect, useState, useRef } from "react";
import { AccountConsole, ConsoleActionGroup, LifecycleFilter } from "@/components/AccountConsole";
import { UserAccessActions } from "@/components/UserAccessActions";
import { AgencyServicesDefaults, AgencyServicesButton } from "@/components/AgencyServices";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, Plus, HandCoins } from "lucide-react";

export default function AdminAgencies() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [lifecycle, setLifecycle] = useState("active");
  const [agencyType, setAgencyType] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const load = (targetPage = page) => {
    const id = ++requestId.current; setLoading(true); setError("");
    api.get("/admin/profiles", { params: { role: "agent", q, lifecycle, agency_type: agencyType, page: typeof targetPage === "number" ? targetPage : page } })
      .then(({ data }) => { if (id === requestId.current) { setAgents(data.items); setTotal(data.total); } })
      .catch(() => { if (id === requestId.current) setError("Impossibile caricare le agenzie. Riprova con Cerca."); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
  };
  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [lifecycle, agencyType]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!form.email.trim() || !form.name.trim() || !form.password.trim()) { toast.error("Compila tutti i campi"); return; }
    setBusy(true);
    try {
      await api.post("/admin/staff", { ...form, role: "agent" });
      toast.success("Agenzia interna creata");
      setOpen(false); setForm({ email: "", name: "", password: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-agencies-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Rete commerciale</span></div>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Agenzie</h1>
          <p className="text-sm text-muted-foreground mt-1">Agenzie interne (create da te) ed esterne (auto-registrate). Assegna un'agenzia ai dealer dalla loro scheda.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => navigate("/app/provvigioni-agenti")} data-testid="go-commissions"><HandCoins className="w-4 h-4" /> Provvigioni</Button>
          <AgencyServicesDefaults /><Button className="gap-2" onClick={() => setOpen(true)} data-testid="create-agency-open"><Plus className="w-4 h-4" /> Crea agenzia interna</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(1); } }} placeholder="Cerca nome, email, P.IVA o codice…" className="max-w-sm" data-testid="agency-search-input" />
        <Button variant="outline" onClick={() => { setPage(1); load(1); }} data-testid="agency-search-button">Cerca</Button>
        <LifecycleFilter value={lifecycle} onChange={setLifecycle} />
        <select value={agencyType} onChange={(e) => setAgencyType(e.target.value)} aria-label="Tipo agenzia" className="h-10 rounded-md border border-border bg-card px-3 text-sm" data-testid="agency-type-filter"><option value="all" data-testid="agency-type-all">Tutti i tipi</option><option value="internal" data-testid="agency-type-internal">Interne</option><option value="external" data-testid="agency-type-external">Esterne</option></select>
      </div>
      <AccountConsole items={agents} total={total} page={page} onPage={(value) => { setPage(value); load(value); }} onChanged={load} loading={loading} error={error} renderTools={(a) => (
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid={`agency-card-${a.id}`}>
            <ConsoleActionGroup title="Accesso agenzia" id={`agency-access-${a.id}`}><UserAccessActions user={a} mode="admin" onChanged={load} /><AgencyServicesButton agent={a} /></ConsoleActionGroup>
          </div>
        )} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="agency-create-dialog">
          <DialogHeader><DialogTitle>Crea agenzia interna</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agency-name" /></div>
            <div><Label>Email</Label><Input type="email" className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agency-email" /></div>
            <div><Label>Password</Label><Input type="text" className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="agency-password" /></div>
            <Button className="w-full" disabled={busy} onClick={create} data-testid="agency-create-submit">{busy ? "Creazione…" : "Crea agenzia"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
